// ---------------------------------------------------------------------------
// TASK-034 — GET /api/communities/:id/content response schemas.
//
// SCOPE: The wire format for the Content Analytics surface (Mini App
// "Content" page). Three concerns are bundled into one endpoint so the
// frontend renders the page from a single fetch:
//
//   1. topPosts        — top 10 messages by engagement in the analysis
//                        window. Per §15.3 the rows NEVER carry text or
//                        captions — only metadata (length, media type,
//                        reply/forward flags) plus aggregated counts and
//                        the ranking score.
//   2. activityHeatmap — 168 cells indexed by (dayOfWeek 0-6, hour 0-23)
//                        over the same window. dayOfWeek follows ISO order
//                        (0 = Monday, 6 = Sunday) so the §6.3.3 hour-of-week
//                        slot id (0..167) matches `dayOfWeek * 24 + hour`.
//                        Used by the heatmap UI; counts cover every
//                        message (not admin-only) so the visualization
//                        reflects community-wide activity.
//   3. optimalSlots    — §6.3.3 "best posting time" computation. Either
//                        an `insufficient: true` payload with actionable
//                        guidance fields, or a populated `slots` array
//                        with relative scores 0-100 and sample sizes.
//                        ADMIN POSTS ONLY (per §6.3.3); the empty-state
//                        copy is owned by the frontend.
//
// `dataAvailable` is FALSE iff the community has no MessageEvent rows in
// the analysis window — signals the Mini App's onboarding empty state for
// the whole page.
// ---------------------------------------------------------------------------
import { z } from "zod";
import { mediaTypeSchema } from "./communities.js";
/**
 * Analysis window length in days. Pinned to 28 here so the schema, the
 * algorithm, and the backend query all share one source of truth. PRD
 * §6.3.3 requires this exact window for the optimal-slot calculation.
 */
export const CONTENT_ANALYSIS_WINDOW_DAYS = 28;
/**
 * Optimal-slot algorithm thresholds (§6.3.3 — minimum data requirements).
 * Exposed so the pure `computeOptimalSlots` function and the test fixtures
 * can reference one set of constants.
 */
export const OPTIMAL_SLOTS_REQUIRED_DAYS = 28;
export const OPTIMAL_SLOTS_REQUIRED_TOTAL_POSTS = 20;
export const OPTIMAL_SLOTS_MIN_SAMPLES_PER_SLOT = 3;
/**
 * Maximum number of `topPosts` rows returned. PRD §6.3.3 task description
 * says "top 10 posts list".
 */
export const CONTENT_TOP_POSTS_LIMIT = 10;
/**
 * Maximum number of `topSlots` rows returned. PRD §6.3.3 says "Top 3 time
 * slots".
 */
export const OPTIMAL_SLOTS_TOP_N = 3;
/**
 * Engagement weights used when ranking topPosts. Mirrors the §6.3.3
 * `post_engagement` formula except that `forwards_count` is dropped — the
 * Telegram Bot API exposes it unreliably (see §6.3.3 metric availability
 * table). Documented at the call site as well.
 */
export const TOP_POSTS_REPLY_WEIGHT = 2;
export const TOP_POSTS_REACTION_WEIGHT = 1;
/**
 * One ranked post in the top-10 list. The shape mirrors
 * `communityOverviewTopPostSchema` (TASK-027) field-for-field with one
 * difference: `score` here is `reactionsCount + repliesCount × 2` per
 * §6.3.3 (vs the simpler `reactions + replies` of the overview top-5).
 *
 * NEVER carries `text` / `caption` / message body — strictly metadata +
 * aggregated counts. Adding such a field is a §15.3 regression.
 */
export const contentTopPostSchema = z.object({
    telegramMessageId: z.string().regex(/^-?\d+$/),
    senderTelegramId: z.string().regex(/^-?\d+$/),
    sentAt: z.string().datetime(),
    contentLength: z.number().int().nonnegative(),
    hasMedia: z.boolean(),
    mediaType: mediaTypeSchema.nullable(),
    isReply: z.boolean(),
    isForwarded: z.boolean(),
    reactionsCount: z.number().int().nonnegative(),
    repliesCount: z.number().int().nonnegative(),
    /**
     * Combined ranking score: `reactions + replies × 2`. Exposed so the
     * frontend can render the underlying signal verbatim without re-deriving.
     */
    score: z.number().nonnegative(),
});
/**
 * One cell in the 24×7 activity heatmap. `dayOfWeek` uses ISO ordering
 * (0 = Monday) so the (dayOfWeek, hour) tuple maps cleanly to a
 * §6.3.3 hour-of-week slot id via `dayOfWeek × 24 + hour`. Cells with
 * zero activity are NOT emitted — the frontend backfills missing cells
 * to keep payload size bounded.
 */
export const contentHeatmapCellSchema = z.object({
    dayOfWeek: z.number().int().min(0).max(6),
    hour: z.number().int().min(0).max(23),
    messages: z.number().int().nonnegative(),
});
/**
 * One slot score in the optimal-slot result. `hourOfWeek` is the §6.3.3
 * key (0..167); `dayOfWeek` and `hour` are denormalized for UI
 * convenience. `relativeScore` is normalized to the best-performing
 * slot in the result (top slot always 100).
 */
export const optimalSlotScoreSchema = z.object({
    hourOfWeek: z.number().int().min(0).max(167),
    dayOfWeek: z.number().int().min(0).max(6),
    hour: z.number().int().min(0).max(23),
    avgEngagement: z.number().nonnegative(),
    sampleSize: z.number().int().min(1),
    relativeScore: z.number().int().min(0).max(100),
});
/**
 * Insufficient-data branch — the Mini App renders the §6.3.3 "Need more
 * data. Post regularly for 4 weeks ..." empty state.
 *
 * `daysRemaining`: number of additional days of data accumulation needed
 *   to reach the 28-day window. 0 once the window is satisfied.
 * `postsRemaining`: additional admin posts needed to reach the
 *   ≥20 admin posts threshold. 0 once the threshold is satisfied.
 * `slotsShort`: count of distinct hour-of-week slots that received
 *   admin posts but were under the ≥3-per-slot floor. Surfaced so the
 *   UI can say "you have signal in N slots, just not enough" — useful
 *   for owners whose schedule is concentrated in a few time windows.
 */
export const optimalSlotsInsufficientSchema = z.object({
    insufficient: z.literal(true),
    windowDays: z.number().int().min(0),
    daysRemaining: z.number().int().min(0),
    postsRemaining: z.number().int().min(0),
    slotsShort: z.number().int().min(0),
    totalAdminPosts: z.number().int().min(0),
});
/**
 * Sufficient-data branch — `slots` carries every slot that passed the
 * ≥3-samples filter (sorted by avgEngagement DESC, ties broken by
 * hourOfWeek ASC for determinism). `topSlots` is the first 3 entries of
 * the same sort, exposed as a separate field so the UI doesn't slice
 * inline. `windowDays` is the actual analysis window (always 28 in the
 * sufficient branch).
 */
export const optimalSlotsSufficientSchema = z.object({
    insufficient: z.literal(false),
    windowDays: z.number().int().min(28),
    totalAdminPosts: z.number().int().min(0),
    slots: z.array(optimalSlotScoreSchema),
    topSlots: z.array(optimalSlotScoreSchema).max(OPTIMAL_SLOTS_TOP_N),
});
/**
 * Discriminated union over `insufficient`. Frontend narrows via
 * `if (result.insufficient)` and gets full type safety on the remaining
 * fields. Mirrors the discriminated-union pattern used by
 * `communityHealthResponseSchema` (TASK-028).
 */
export const optimalSlotsResultSchema = z.discriminatedUnion("insufficient", [
    optimalSlotsInsufficientSchema,
    optimalSlotsSufficientSchema,
]);
/**
 * Final response shape for GET /api/communities/:id/content.
 */
export const contentAnalyticsResponseSchema = z.object({
    /** UTC analysis window (last 28 days ending at `generatedAt`). */
    windowDays: z.literal(CONTENT_ANALYSIS_WINDOW_DAYS),
    /** FALSE iff the community has zero MessageEvents in the window. */
    dataAvailable: z.boolean(),
    topPosts: z.array(contentTopPostSchema).max(CONTENT_TOP_POSTS_LIMIT),
    activityHeatmap: z.array(contentHeatmapCellSchema),
    optimalSlots: optimalSlotsResultSchema,
    generatedAt: z.string().datetime(),
});
//# sourceMappingURL=content.js.map